import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios, { FILE_URL } from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout'; // Bayi de aynı düzeni kullanabilir
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash, FaShoppingCart, FaSearch, FaChevronDown, FaChevronRight, FaFilter, FaTimes } from 'react-icons/fa';

const ProductList = () => {
    const { t, i18n } = useTranslation();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryTree, setCategoryTree] = useState([]); // Kategori ağacı
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showDealerPrice, setShowDealerPrice] = useState({}); // Hangi ürünün fiyatı açık?
    const [searchTerm, setSearchTerm] = useState(''); // Arama state'i
    const [expandedCategories, setExpandedCategories] = useState({}); // Açık olan kategoriler
    const [quickViewProduct, setQuickViewProduct] = useState(null); // Hızlı bakış yapılan ürün
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true); // Yükleniyor durumu
    const itemsPerPage = 20;
    
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user'));
    
    // Sepet işlemleri
    const { addToCart } = useCart();
    const [quantities, setQuantities] = useState({}); // Her ürün için girilen miktar
    const [selectedVariations, setSelectedVariations] = useState({}); // Her ürün için seçilen varyasyon

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchProducts(), fetchCategories()]);
            setLoading(false);
        };
        loadData();
    }, []);

    // Filtre değişince sayfayı başa al
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory]);

    const fetchProducts = async () => {
        try {
            const res = await axios.get('/products');
            setProducts(res.data);
        } catch (error) {
            console.error('Ürünler yüklenemedi');
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get('/categories');
            setCategories(res.data);
            setCategoryTree(buildCategoryTree(res.data));
        } catch (error) {
            console.error('Kategoriler yüklenemedi');
        }
    };

    // Kategorileri Ağaç Yapısına Çevir
    const buildCategoryTree = (cats) => {
        const map = {};
        const roots = [];
        cats.forEach(cat => map[cat.id] = { ...cat, children: [] });
        cats.forEach(cat => {
            if (cat.parent_id && map[cat.parent_id]) {
                map[cat.parent_id].children.push(map[cat.id]);
            } else {
                roots.push(map[cat.id]);
            }
        });
        return roots;
    };

    // Göz İkonuna Tıklama (Fiyat Göster/Gizle)
    const togglePrice = (productId) => {
        setShowDealerPrice(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    // Varyasyon Seçimi
    const handleVariationChange = (productId, variation) => {
        setSelectedVariations(prev => ({ ...prev, [productId]: variation }));
    };

    // Sepete Ekle Butonu
    const handleAddToCart = (product) => {
        const variation = selectedVariations[product.id] || product.variations[0];
        const quantity = quantities[product.id] || product.min_order_limit || 1;

        if (!variation) {
            toast.error('Lütfen bir seçenek (renk/ölçü) seçin.');
            return;
        }

        // Stok Kontrolü
        if (variation.stock_quantity < quantity) {
            toast.error(`Yetersiz stok! En fazla ${variation.stock_quantity} adet alabilirsiniz.`);
            return;
        }

        addToCart(product, variation, quantity, variation.retail_price);
        toast.success(t('messages.added_to_cart'));
    };

    // Kategori Filtreleme
    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory 
            ? product.categories?.some(c => c.id === Number(selectedCategory))
            : true;
        
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = product.name.toLowerCase().includes(searchLower) ||
                              product.variations?.some(v => v.sku?.toLowerCase().includes(searchLower));

        // Sadece aktif ürünleri göster (Admin olsa bile bu sayfada pasifleri görmesin)
        return matchesCategory && matchesSearch && product.is_active;
    });

    // Sayfalama Mantığı
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Kategori Açma/Kapama
    const toggleExpand = (id) => {
        setExpandedCategories(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Hızlı Bakış Aç/Kapa
    const handleQuickView = (product) => {
        setQuickViewProduct(product);
    };
    const closeQuickView = () => {
        setQuickViewProduct(null);
    };

    // Kategori Bileşeni (Recursive)
    const renderCategory = (cat) => {
        const isSelected = Number(selectedCategory) === cat.id;
        const hasChildren = cat.children && cat.children.length > 0;
        const isExpanded = expandedCategories[cat.id];

        return (
            <div key={cat.id} className="mb-2">
                <div 
                    className={`d-flex align-items-center justify-content-between p-2 rounded-3 transition-all ${isSelected ? 'bg-primary text-white shadow fw-bold' : 'text-dark bg-light hover-bg-gray fw-medium'}`}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => setSelectedCategory(isSelected ? '' : cat.id)}
                >
                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                        {/* Yaprak kategoriler için küçük ikon */}
                        {!hasChildren && <span className={`rounded-circle ${isSelected ? 'bg-white' : 'bg-secondary'}`} style={{width: '6px', height: '6px', opacity: 0.6}}></span>}
                        <span className="text-truncate" style={{ fontSize: '1rem' }}>
                            {i18n.language === 'fr' ? (cat.name_fr || cat.name) : cat.name}
                        </span>
                    </div>
                    {hasChildren && (
                        <div 
                            onClick={(e) => { e.stopPropagation(); toggleExpand(cat.id); }} 
                            className={`p-1 rounded-circle d-flex align-items-center justify-content-center ${isSelected ? 'text-white' : 'text-muted'}`}
                            style={{ width: '24px', height: '24px', backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : 'transparent' }}
                        >
                            {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                        </div>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div className="ms-3 border-start ps-2 mt-2 border-primary border-opacity-25 animate__animated animate__fadeIn">
                        {cat.children.map(child => renderCategory(child))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <AdminLayout>
            <div className="container-fluid p-0">
                <div className="row g-4">
                    {/* Sol Sidebar - Filtreler */}
                    <div className="col-md-4 col-lg-3 col-xl-2">
                        <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px', zIndex: 1, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                    <h5 className="fw-bold m-0 d-flex align-items-center gap-2 text-primary">
                                        <FaFilter /> {t('products.filters')}
                                    </h5>
                                    {(selectedCategory || searchTerm) && (
                                        <button 
                                            className="btn btn-sm btn-link text-danger text-decoration-none p-0 d-flex align-items-center gap-1" 
                                            style={{fontSize: '0.8rem'}}
                                            onClick={() => { setSelectedCategory(''); setSearchTerm(''); }}
                                        >
                                            <FaTimes /> {t('common.clear_filters')}
                                        </button>
                                    )}
                                </div>

                                {/* Kategoriler */}
                                <div>
                                    <label className="form-label fw-bold text-dark text-uppercase mb-3" style={{fontSize: '0.95rem'}}>{t('products.categories')}</label>
                                    <div className="category-tree">
                                        {categoryTree.map(cat => renderCategory(cat))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sağ Taraf - Ürün Listesi */}
                    <div className="col-md-8 col-lg-9 col-xl-10">
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h2 className="fw-bold m-0">{t('products.title')}</h2>
                                <span className="text-muted">{filteredProducts.length} {t('products.found')}</span>
                            </div>
                            <div className="input-group input-group-lg shadow-sm">
                                <span className="input-group-text bg-white border-end-0"><FaSearch className="text-muted" /></span>
                                <input 
                                    type="text" 
                                    className="form-control border-start-0" 
                                    placeholder={t('products.search_placeholder')} 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('common.loading')}</span>
                                </div>
                            </div>
                        ) : (
                        <div className="row">
                            {currentItems.map(product => {
                                // Varsayılan varyasyon (ilk varyasyon)
                                const currentVariation = selectedVariations[product.id] || product.variations?.[0];
                                const isPriceVisible = showDealerPrice[product.id];

                                // Stok Durumu Görselleştirmesi
                                let stockBadge = null;
                                let borderClass = "border-0";

                                if (currentVariation) {
                                    if (Number(currentVariation.stock_quantity) <= 0) {
                                        stockBadge = (
                                            <span className="badge bg-danger position-absolute top-0 start-0 m-2 shadow-sm" style={{ zIndex: 5 }}>
                                                {t('products.out_of_stock')}
                                            </span>
                                        );
                                        borderClass = "border border-danger";
                                    } else if (Number(currentVariation.stock_quantity) < 20) {
                                        stockBadge = (
                                            <span className="badge bg-warning text-dark position-absolute top-0 start-0 m-2 shadow-sm" style={{ zIndex: 5 }}>
                                                Son {currentVariation.stock_quantity}
                                            </span>
                                        );
                                        borderClass = "border border-warning";
                                    }
                                }

                                return (
                                    <div key={product.id} className="col-sm-6 col-lg-4 col-xl-3 mb-4">
                                        <div className={`card h-100 shadow-sm transition-hover ${borderClass}`}>
                                            {/* Ürün Resmi */}
                                            <div style={{ position: 'relative' }}>
                                                {stockBadge}
                                                <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                    <div style={{ height: '220px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', position: 'relative' }}>
                                                        {product.main_image ? (
                                                        <img src={`${FILE_URL}/${product.main_image}`} className="card-img-top" alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }} />
                                                        ) : (
                                                            <span className="text-muted">{t('products.no_image')}</span>
                                                        )}
                                                    </div>
                                                </Link>
                                                <button 
                                                    className="btn btn-light btn-sm position-absolute top-0 end-0 m-2 rounded-circle shadow-sm d-flex align-items-center justify-content-center text-muted hover-primary"
                                                    style={{ width: '35px', height: '35px', zIndex: 5 }}
                                                    onClick={() => handleQuickView(product)}
                                                    title="Hızlı Bakış"
                                                >
                                                    <FaEye size={16} />
                                                </button>
                                            </div>
                                            <div className="card-body d-flex flex-column">
                                                <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                    <h6 className="card-title fw-bold text-truncate mb-1" title={product.name}>{product.name}</h6>
                                                </Link>

                                                {/* Sadece Admin İçin Stok Gösterimi */}
                                                {user?.role === 'admin' && (
                                                    <div className={`small fw-bold mb-2 ${(product.total_stock || 0) < 150 ? 'text-danger' : 'text-success'}`}>
                                                        Toplam Stok: {product.total_stock || 0} {product.unit_type === 'm2' ? 'm²' : product.unit_type === 'pallet' ? 'Palet' : 'Adet'}
                                                        {(product.total_stock || 0) < 150 && <span className="ms-1">⚠️</span>}
                                                    </div>
                                                )}

                                                <p className="text-muted small mb-3">
                                                    {t('products.unit')}: {product.unit_type === 'm2' ? t('common.unit_m2') : product.unit_type === 'pallet' ? t('common.unit_pallet') : t('common.unit_piece')}
                                                </p>
                                                
                                                {/* Varyasyon Seçimi */}
                                                {product.variations?.length > 1 && (
                                                    <div className="mb-3">
                                                        <select 
                                                            className="form-select form-select-sm bg-light border-0"
                                                            onChange={(e) => {
                                                                const v = product.variations.find(v => v.id === Number(e.target.value));
                                                                handleVariationChange(product.id, v);
                                                            }}
                                                        >
                                                            {product.variations.map(v => (
                                                                <option key={v.id} value={v.id}>
                                                                    {v.size} - {v.color}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                <div className="mt-auto">
                                                    {/* Fiyat Alanı - GİZLİ GÖZ */}
                                                    {currentVariation && (
                                                        <div className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-end">
                                                                <div>
                                                                    <small className="text-muted d-block" style={{fontSize: '0.75rem'}}>{t('products.sales_price')}</small>
                                                                    <span className="fw-bold text-primary fs-5">
                                                                        {Number(currentVariation.retail_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                                                                    </span>
                                                                </div>
                                                                <button 
                                                                    className="btn btn-link text-muted p-0" 
                                                                    onClick={() => togglePrice(product.id)}
                                                                    title={t('product_detail.dealer_price')}
                                                                >
                                                                    {isPriceVisible ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                                                </button>
                                                            </div>
                                                            
                                                            {/* Sadece Göz Açıksa Görünür */}
                                                            {isPriceVisible && (
                                                                <div className="mt-2 pt-2 border-top border-dashed">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <span className="text-danger fw-bold small">
                                                                            {t('products.dealer_price')}: {Number(currentVariation.dealer_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                                                                        </span>
                                                                        <span className="badge bg-light text-dark border">{t('products.stock')}: {currentVariation.stock_quantity}</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Miktar ve Sepete Ekle */}
                                                    <div className="d-flex gap-2">
                                                        <input 
                                                            type="number" 
                                                            className="form-control form-control-sm text-center" 
                                                            placeholder={t('common.unit_piece')}
                                                            min={product.min_order_limit}
                                                            defaultValue={product.min_order_limit}
                                                            style={{ width: '60px' }}
                                                            onChange={(e) => setQuantities({...quantities, [product.id]: e.target.value})}
                                                        />
                                                        <button 
                                                            className="btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2 text-white"
                                                            style={{ backgroundColor: '#11848f', borderColor: '#11848f' }}
                                                            onClick={() => handleAddToCart(product)}
                                                        >
                                                            <FaShoppingCart /> {t('products.add_to_cart')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            
                            {filteredProducts.length === 0 && (
                                <div className="col-12 text-center py-5">
                                    <div className="text-muted">
                                        <FaSearch size={48} className="mb-3 opacity-25" />
                                        <h5>{t('products.not_found_title')}</h5>
                                        <p>{t('products.not_found_desc')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Pagination Footer */}
                        {!loading && filteredProducts.length > 0 && (
                            <div className="d-flex align-items-center mt-4 pt-3 border-top gap-3">
                                {filteredProducts.length > itemsPerPage && (
                                    <nav>
                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link border-0 rounded-circle mx-1" onClick={() => paginate(currentPage - 1)}>&laquo;</button>
                                            </li>
                                            {[...Array(totalPages)].map((_, i) => (
                                                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                    <button className="page-link border-0 rounded-circle mx-1" onClick={() => paginate(i + 1)}>
                                                        {i + 1}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link border-0 rounded-circle mx-1" onClick={() => paginate(currentPage + 1)}>&raquo;</button>
                                            </li>
                                        </ul>
                                    </nav>
                                )}
                                <div className="text-muted small">
                                    {t('products.total_items')} <strong>{filteredProducts.length}</strong> {t('products.items_from')} <strong>{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredProducts.length)}</strong> {t('products.showing_range')}.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Hızlı Bakış Modalı */}
            {quickViewProduct && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="modal-header border-0 position-absolute top-0 end-0" style={{ zIndex: 10 }}>
                                <button type="button" className="btn-close bg-white p-2 m-2 shadow-sm rounded-circle" onClick={closeQuickView}></button>
                            </div>
                            <div className="modal-body p-0">
                                <div className="row g-0">
                                    <div className="col-md-6 bg-light d-flex align-items-center justify-content-center p-4">
                                        {quickViewProduct.main_image ? (
                                            <img 
                                                src={`${FILE_URL}/${quickViewProduct.main_image}`} 
                                                className="img-fluid rounded shadow-sm" 
                                                alt={quickViewProduct.name} 
                                                style={{ maxHeight: '300px' }}
                                            />
                                        ) : (
                                            <span className="text-muted">{t('product_detail.no_image')}</span>
                                        )}
                                    </div>
                                    <div className="col-md-6 p-4 d-flex flex-column">
                                        <h4 className="fw-bold mb-2">{quickViewProduct.name}</h4>
                                        <div className="mb-3">
                                            {quickViewProduct.categories?.map(cat => (
                                                <span key={cat.id} className="badge bg-light text-dark border me-1">{cat.name}</span>
                                            ))}
                                        </div>
                                        
                                        <div className="mb-3">
                                            <span className="text-muted small">{t('products.unit')}: </span>
                                            <span className="fw-bold">{quickViewProduct.unit_type === 'm2' ? t('common.unit_m2') : quickViewProduct.unit_type === 'pallet' ? t('common.unit_pallet') : t('common.unit_piece')}</span>
                                        </div>

                                        {/* Fiyat Gösterimi (İlk varyasyon baz alınarak) */}
                                        {quickViewProduct.variations && quickViewProduct.variations.length > 0 && (
                                            <div className="mb-4">
                                                <h3 className="text-primary fw-bold mb-0">
                                                    {Number(quickViewProduct.variations[0].retail_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                                                </h3>
                                                <small className="text-muted">{t('product_detail.vat_included')}</small>
                                            </div>
                                        )}

                                        <div className="mt-auto d-flex gap-2">
                                            <Link to={`/products/${quickViewProduct.id}`} className="btn btn-primary flex-grow-1 rounded-pill">{t('product_detail.inspect')}</Link>
                                            <button className="btn btn-outline-secondary rounded-pill" onClick={closeQuickView}>{t('common.select')}</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default ProductList;
